'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { apiFetch, ApiError } from '../lib/api';
import { useToast } from './Toast';
import {
  type EventType,
  type EventStatus,
  type CalendarEvent,
  type ChecklistItem,
  type EventReminder,
  type UserOption,
  type TeamOption,
  toLocalDateInput,
  toLocalTimeInput,
  combineDateTime,
  getRecurrenceRule,
  parseRecurrenceType,
  parseRecurrenceInterval,
} from './EventFormModal';

type RecurrenceType = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export type UseEventFormOptions = {
  event: CalendarEvent | null;
  defaultType: EventType;
  defaultDate?: Date;
  defaultEndDate?: Date;
  /** When true, the hook resets form state (used by modal on open). For inline, set to true always. */
  active: boolean;
  onSaved?: (event: CalendarEvent, isNew: boolean) => void;
  onDeleted?: (id: number) => void;
  onClose?: () => void;
  editLabel?: string;
  newLabel?: string;
  /** Defaults for a NEW event (e.g. bind to entity via refType/refId or contact/account/lead). */
  newEventDefaults?: {
    title?: string | null; description?: string | null;
    refType?: string | null; refId?: string | null;
    accountId?: number | null; accountName?: string | null; accountPohodaId?: string | null;
    contactId?: number | null; contactName?: string | null;
    leadId?: number | null; leadName?: string | null;
    phoneNumber?: string | null;
    impulseType?: string | null; impulseKey?: string | null;
  } | null;
};

export type UseEventFormReturn = {
  // State
  editingEvent: CalendarEvent | null;
  setEditingEvent: (e: CalendarEvent | null) => void;
  deleteId: number | null;
  setDeleteId: (id: number | null) => void;
  saving: boolean;

  // Form fields
  formType: EventType;
  setFormType: (t: EventType) => void;
  formTitle: string;
  setFormTitle: (s: string) => void;
  formDescription: string;
  setFormDescription: (s: string) => void;
  formAllDay: boolean;
  setFormAllDay: (b: boolean) => void;
  formStatus: EventStatus;
  setFormStatus: (s: EventStatus) => void;
  formPriority: string;
  setFormPriority: (s: string) => void;
  formLocation: string;
  setFormLocation: (s: string) => void;
  formParticipants: string;
  setFormParticipants: (s: string) => void;
  formPhone: string;
  setFormPhone: (s: string) => void;
  formCallResult: string;
  setFormCallResult: (s: string) => void;
  formImChannel: string;
  setFormImChannel: (s: string) => void;
  formDuration: string;
  setFormDuration: (s: string) => void;
  formRecurrence: RecurrenceType;
  setFormRecurrence: (r: RecurrenceType) => void;
  formRecurrenceInterval: number;
  setFormRecurrenceInterval: (n: number) => void;
  formRecurrenceEndDate: string;
  setFormRecurrenceEndDate: (s: string) => void;
  formRecurrenceEndTime: string;
  setFormRecurrenceEndTime: (s: string) => void;
  formDueDateDate: string;
  setFormDueDateDate: (s: string) => void;
  formDueDateTime: string;
  setFormDueDateTime: (s: string) => void;
  formColor: string;
  setFormColor: (s: string) => void;
  formChecklist: ChecklistItem[];
  setFormChecklist: (c: ChecklistItem[]) => void;
  newChecklistText: string;
  setNewChecklistText: (s: string) => void;
  formReminders: EventReminder[];
  setFormReminders: (r: EventReminder[]) => void;
  formAssigneeIds: number[];
  setFormAssigneeIds: (ids: number[]) => void;
  formWatcherIds: number[];
  setFormWatcherIds: (ids: number[]) => void;
  formTeamId: number | null;
  setFormTeamId: (id: number | null) => void;
  formPrivate: boolean;
  setFormPrivate: (b: boolean) => void;
  formGoogleSync: boolean;
  setFormGoogleSync: (b: boolean) => void;
  formAccountId: number | null;
  setFormAccountId: (id: number | null) => void;
  formAccountName: string | null;
  setFormAccountName: (s: string | null) => void;
  formAccountPohodaId: string | null;
  setFormAccountPohodaId: (s: string | null) => void;
  formContactId: number | null;
  setFormContactId: (id: number | null) => void;
  formContactName: string | null;
  setFormContactName: (s: string | null) => void;
  formLeadId: number | null;
  setFormLeadId: (id: number | null) => void;
  formLeadName: string | null;
  setFormLeadName: (s: string | null) => void;
  formRefType: string | null;
  formRefId: string | null;
  formImpulseType: string | null;
  formStartDate: string;
  setFormStartDate: (s: string) => void;
  formStartTime: string;
  setFormStartTime: (s: string) => void;
  formEndDate: string;
  setFormEndDate: (s: string) => void;
  formEndTime: string;
  setFormEndTime: (s: string) => void;

  // Derived
  formStart: string;
  formEnd: string;
  isDirty: boolean;
  checklistProgress: number;

  // Helpers
  addChecklistItem: () => void;
  toggleChecklistItem: (idx: number) => void;
  removeChecklistItem: (idx: number) => void;
  addReminder: () => void;
  updateReminder: (idx: number, field: 'minutes' | 'type' | 'refPoint' | 'absoluteTime', value: string | number) => void;
  removeReminder: (idx: number) => void;
  toggleAssignee: (uid: number) => void;
  toggleWatcher: (uid: number) => void;
  handleSave: (closeAfter?: boolean) => Promise<void>;
  handleDelete: () => Promise<void>;
  cancelSnooze: () => Promise<void>;
  autoSaveState: 'idle' | 'saving' | 'saved' | 'error';
};

export function useEventForm(opts: UseEventFormOptions): UseEventFormReturn {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const { showToast } = useToast();
  const { event, defaultType, defaultDate, defaultEndDate, active, onSaved, onDeleted, onClose, editLabel, newLabel, newEventDefaults } = opts;

  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Form state
  const [formType, setFormType] = useState<EventType>(defaultType);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAllDay, setFormAllDay] = useState(false);
  const [formStatus, setFormStatus] = useState<EventStatus>('PENDING');
  const [formPriority, setFormPriority] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formParticipants, setFormParticipants] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formCallResult, setFormCallResult] = useState('');
  const [formImChannel, setFormImChannel] = useState('');
  const [formDuration, setFormDuration] = useState('');
  const [formRecurrence, setFormRecurrence] = useState<RecurrenceType>('NONE');
  const [formRecurrenceInterval, setFormRecurrenceInterval] = useState(1);
  const [formRecurrenceEndDate, setFormRecurrenceEndDate] = useState('');
  const [formRecurrenceEndTime, setFormRecurrenceEndTime] = useState('');
  const [formDueDateDate, setFormDueDateDate] = useState('');
  const [formDueDateTime, setFormDueDateTime] = useState('');
  const [formColor, setFormColor] = useState('');
  const [formChecklist, setFormChecklist] = useState<ChecklistItem[]>([]);
  const [newChecklistText, setNewChecklistText] = useState('');
  const [formReminders, setFormReminders] = useState<EventReminder[]>([]);
  const [formAssigneeIds, setFormAssigneeIds] = useState<number[]>([]);
  const [formWatcherIds, setFormWatcherIds] = useState<number[]>([]);
  const [formTeamId, setFormTeamId] = useState<number | null>(null);
  const [formPrivate, setFormPrivate] = useState(false);
  const [formGoogleSync, setFormGoogleSync] = useState(true);
  const [formAccountId, setFormAccountId] = useState<number | null>(null);
  const [formAccountName, setFormAccountName] = useState<string | null>(null);
  const [formAccountPohodaId, setFormAccountPohodaId] = useState<string | null>(null);
  const [formContactId, setFormContactId] = useState<number | null>(null);
  const [formContactName, setFormContactName] = useState<string | null>(null);
  const [formLeadId, setFormLeadId] = useState<number | null>(null);
  const [formLeadName, setFormLeadName] = useState<string | null>(null);
  const [formStartDate, setFormStartDate] = useState(toLocalDateInput(new Date()));
  const [formStartTime, setFormStartTime] = useState('09:00');
  const [formEndDate, setFormEndDate] = useState(toLocalDateInput(new Date()));
  const [formEndTime, setFormEndTime] = useState('10:00');

  const [formRefType, setFormRefType] = useState<string | null>(null);
  const [formRefId, setFormRefId] = useState<string | null>(null);
  const [formImpulseType, setFormImpulseType] = useState<string | null>(null);
  const [formImpulseKey, setFormImpulseKey] = useState<string | null>(null);

  // Initialize/reset form state
  useEffect(() => {
    if (!active) return;
    if (event) {
      setEditingEvent(event);
      setFormType(event.type);
      setFormTitle(event.title);
      setFormDescription(event.description || '');
      const startDt = new Date(event.startTime);
      setFormStartDate(toLocalDateInput(startDt));
      setFormStartTime(toLocalTimeInput(startDt));
      if (event.endTime) {
        const endDt = new Date(event.endTime);
        setFormEndDate(toLocalDateInput(endDt));
        setFormEndTime(toLocalTimeInput(endDt));
      } else {
        setFormEndDate(toLocalDateInput(startDt));
        setFormEndTime('10:00');
      }
      setFormAllDay(event.allDay);
      setFormStatus(event.status);
      setFormPriority(event.priority || '');
      setFormLocation(event.location || '');
      setFormParticipants(event.participants || '');
      setFormPhone(event.phoneNumber || '');
      setFormCallResult(event.callResult || '');
      setFormImChannel(event.imChannel || '');
      setFormDuration(event.durationMinutes?.toString() || '');
      setFormRecurrence(parseRecurrenceType(event.recurrenceRule));
      setFormRecurrenceInterval(parseRecurrenceInterval(event.recurrenceRule));
      if (event.recurrenceEndDate) {
        const recEndDt = new Date(event.recurrenceEndDate);
        setFormRecurrenceEndDate(toLocalDateInput(recEndDt));
        setFormRecurrenceEndTime(toLocalTimeInput(recEndDt));
      } else {
        setFormRecurrenceEndDate('');
        setFormRecurrenceEndTime('');
      }
      if (event.dueDate) {
        const dueDt = new Date(event.dueDate);
        setFormDueDateDate(toLocalDateInput(dueDt));
        setFormDueDateTime(toLocalTimeInput(dueDt));
      } else {
        setFormDueDateDate('');
        setFormDueDateTime('');
      }
      setFormColor(event.color || '');
      setFormChecklist(event.checklist || []);
      setNewChecklistText('');
      setFormReminders((event.reminders || []).filter(r => r.absoluteTime == null || r.refPoint === 'ABSOLUTE'));
      setFormAssigneeIds(event.assigneeIds || []);
      setFormWatcherIds(event.watcherIds || []);
      setFormTeamId(event.teamId || null);
      setFormPrivate(event.isPrivate || false);
      setFormGoogleSync(event.googleSync !== false);
      setFormAccountId(event.accountId ?? null);
      setFormAccountName(event.accountName ?? null);
      setFormAccountPohodaId(event.accountPohodaId ?? null);
      setFormContactId(event.contactId ?? null);
      setFormContactName(event.contactName ?? null);
      setFormLeadId(event.leadId ?? null);
      setFormLeadName(event.leadName ?? null);
      setFormRefType(event.refType ?? null);
      setFormRefId(event.refId ?? null);
      setFormImpulseType(event.impulseType ?? null);
      setFormImpulseKey(event.impulseKey ?? null);
    } else {
      const baseDate = defaultDate ? new Date(defaultDate) : new Date();
      if (!defaultDate) baseDate.setMinutes(baseDate.getMinutes() - baseDate.getMinutes() % 5);
      const endDate = defaultEndDate ? new Date(defaultEndDate) : new Date(baseDate);
      if (!defaultEndDate) endDate.setMinutes(endDate.getMinutes() + 60);

      setEditingEvent(null);
      setFormType(defaultType);
      setFormTitle(newEventDefaults?.title ?? '');
      setFormDescription(newEventDefaults?.description ?? '');
      setFormStartDate(toLocalDateInput(baseDate));
      setFormStartTime(toLocalTimeInput(baseDate));
      setFormEndDate(toLocalDateInput(endDate));
      setFormEndTime(toLocalTimeInput(endDate));
      setFormAllDay(false);
      setFormStatus('PENDING');
      setFormPriority('');
      setFormLocation('');
      setFormParticipants('');
      setFormPhone(newEventDefaults?.phoneNumber ?? '');
      setFormCallResult('');
      setFormImChannel('');
      setFormDuration('');
      setFormRecurrence('NONE');
      setFormRecurrenceEndDate('');
      setFormRecurrenceEndTime('');
      setFormDueDateDate('');
      setFormDueDateTime('');
      setFormColor('');
      setFormChecklist([]);
      setNewChecklistText('');
      setFormReminders([]);
      setFormAssigneeIds(userId ? [userId] : []);
      setFormWatcherIds([]);
      setFormTeamId(null);
      setFormPrivate(false);
      setFormGoogleSync(true);
      setFormAccountId(newEventDefaults?.accountId ?? null);
      setFormAccountName(newEventDefaults?.accountName ?? null);
      setFormAccountPohodaId(newEventDefaults?.accountPohodaId ?? null);
      setFormContactId(newEventDefaults?.contactId ?? null);
      setFormContactName(newEventDefaults?.contactName ?? null);
      setFormLeadId(newEventDefaults?.leadId ?? null);
      setFormLeadName(newEventDefaults?.leadName ?? null);
      setFormRefType(newEventDefaults?.refType ?? null);
      setFormRefId(newEventDefaults?.refId ?? null);
      setFormImpulseType(newEventDefaults?.impulseType ?? null);
      setFormImpulseKey(newEventDefaults?.impulseKey ?? null);
    }
    setDeleteId(null);
  }, [active, event, defaultDate, defaultEndDate, defaultType, userId, newEventDefaults]);

  const formStart = useMemo(() => combineDateTime(formStartDate, formStartTime), [formStartDate, formStartTime]);
  const formEnd = useMemo(() => combineDateTime(formEndDate, formEndTime), [formEndDate, formEndTime]);

  const normalizeTs = (ts: string | null | undefined): string | null => {
    if (!ts) return null;
    return ts.length > 16 ? ts.slice(0, 16) : ts;
  };

  const isDirty = useMemo(() => {
    if (!editingEvent) return true;
    if (formType !== editingEvent.type) return true;
    if (formTitle !== (editingEvent.title || '')) return true;
    if (formDescription !== (editingEvent.description || '')) return true;
    if (formStart !== normalizeTs(editingEvent.startTime)) return true;
    const expectedEnd = formType === 'TASK'
      ? (formDueDateDate ? combineDateTime(formDueDateDate, formDueDateTime || '00:00') : null)
      : (formEnd || null);
    if (expectedEnd !== normalizeTs(editingEvent.endTime)) return true;
    if (formAllDay !== editingEvent.allDay) return true;
    if (formStatus !== editingEvent.status) return true;
    if ((formPriority || null) !== (editingEvent.priority || null)) return true;
    if ((formLocation || null) !== (editingEvent.location || null)) return true;
    if ((formParticipants || null) !== (editingEvent.participants || null)) return true;
    if ((formPhone || null) !== (editingEvent.phoneNumber || null)) return true;
    if ((formCallResult || null) !== (editingEvent.callResult || null)) return true;
    if ((formImChannel || null) !== (editingEvent.imChannel || null)) return true;
    if ((formDuration ? parseInt(formDuration) : null) !== (editingEvent.durationMinutes || null)) return true;
    if (getRecurrenceRule(formRecurrence, formRecurrenceInterval) !== (editingEvent.recurrenceRule || null)) return true;
    const expectedRecEnd = formRecurrenceEndDate ? combineDateTime(formRecurrenceEndDate, formRecurrenceEndTime || '00:00') : null;
    if (expectedRecEnd !== normalizeTs(editingEvent.recurrenceEndDate)) return true;
    const expectedDue = formDueDateDate ? combineDateTime(formDueDateDate, formDueDateTime || '00:00') : null;
    if (expectedDue !== normalizeTs(editingEvent.dueDate)) return true;
    if ((formColor || null) !== (editingEvent.color || null)) return true;
    if (JSON.stringify(formChecklist.map(i => ({ text: i.text, done: i.done }))) !== JSON.stringify((editingEvent.checklist || []).map(i => ({ text: i.text, done: i.done })))) return true;
    if (JSON.stringify(formReminders.map(r => ({ minutes: r.minutes, type: r.type, refPoint: r.refPoint || 'START', absoluteTime: r.absoluteTime || null }))) !== JSON.stringify((editingEvent.reminders || []).filter(r => r.absoluteTime == null || r.refPoint === 'ABSOLUTE').map(r => ({ minutes: r.minutes, type: r.type, refPoint: r.refPoint || 'START', absoluteTime: r.absoluteTime || null })))) return true;
    if (JSON.stringify([...formAssigneeIds].sort()) !== JSON.stringify([...(editingEvent.assigneeIds || [])].sort())) return true;
    if (JSON.stringify([...formWatcherIds].sort()) !== JSON.stringify([...(editingEvent.watcherIds || [])].sort())) return true;
    if ((formTeamId || null) !== (editingEvent.teamId || null)) return true;
    if (formPrivate !== (editingEvent.isPrivate || false)) return true;
    if (formGoogleSync !== (editingEvent.googleSync !== false)) return true;
    if ((formAccountId ?? null) !== (editingEvent.accountId ?? null)) return true;
    if ((formAccountName ?? null) !== (editingEvent.accountName ?? null)) return true;
    if ((formContactId ?? null) !== (editingEvent.contactId ?? null)) return true;
    if ((formContactName ?? null) !== (editingEvent.contactName ?? null)) return true;
    if ((formLeadId ?? null) !== (editingEvent.leadId ?? null)) return true;
    if ((formLeadName ?? null) !== (editingEvent.leadName ?? null)) return true;
    return false;
  }, [editingEvent, formType, formTitle, formDescription, formStart, formEnd, formAllDay, formStatus, formPriority, formLocation, formParticipants, formPhone, formCallResult, formImChannel, formDuration, formRecurrence, formRecurrenceInterval, formRecurrenceEndDate, formRecurrenceEndTime, formDueDateDate, formDueDateTime, formColor, formChecklist, formReminders, formAssigneeIds, formWatcherIds, formTeamId, formPrivate, formGoogleSync, formAccountId, formAccountName, formContactId, formContactName, formLeadId, formLeadName]);

  // Auto-fix deadline/end date
  useEffect(() => {
    const nowMs = Date.now();
    if (formStart) {
      const startMs = new Date(formStart).getTime();
      if (formType === 'TASK' && formDueDateDate) {
        const dueMs = new Date(combineDateTime(formDueDateDate, formDueDateTime || '00:00')).getTime();
        if (dueMs < startMs || dueMs < nowMs) {
          setFormDueDateDate(formStartDate);
          setFormDueDateTime(formStartTime);
        }
      }
      if (formType !== 'TASK' && !formAllDay && formEndDate) {
        const endMs = new Date(combineDateTime(formEndDate, formEndTime)).getTime();
        if (endMs < startMs) {
          setFormEndDate(formStartDate);
          setFormEndTime(formStartTime);
        }
      }
    }
  }, [formStart, formType, formAllDay, formDueDateDate, formDueDateTime, formStartDate, formStartTime, formEndDate, formEndTime]);

  // Auto-calculate duration
  useEffect(() => {
    if (formStart && formEnd && !formAllDay) {
      const start = new Date(formStart).getTime();
      const end = new Date(formEnd).getTime();
      const mins = Math.round((end - start) / 60000);
      if (mins > 0 && formDuration !== mins.toString()) {
        setFormDuration(mins.toString());
      }
    }
  }, [formStart, formEnd, formAllDay, formDuration]);

  const checklistProgress = useMemo(() => {
    if (formChecklist.length === 0) return 0;
    return Math.round((formChecklist.filter(i => i.done).length / formChecklist.length) * 100);
  }, [formChecklist]);

  const addChecklistItem = useCallback(() => {
    if (!newChecklistText.trim()) return;
    setFormChecklist([...formChecklist, { text: newChecklistText.trim(), done: false, sortOrder: formChecklist.length }]);
    setNewChecklistText('');
  }, [newChecklistText, formChecklist]);

  const toggleChecklistItem = useCallback((idx: number) => {
    setFormChecklist(formChecklist.map((item, i) => i === idx ? { ...item, done: !item.done } : item));
  }, [formChecklist]);

  const removeChecklistItem = useCallback((idx: number) => {
    setFormChecklist(formChecklist.filter((_, i) => i !== idx));
  }, [formChecklist]);

  const addReminder = useCallback(() => {
    setFormReminders([...formReminders, { minutes: 5, type: 'APP', refPoint: (formType === 'TASK' && formDueDateDate) ? 'DEADLINE' : 'START', sent: false }]);
  }, [formReminders, formType, formDueDateDate]);

  const updateReminder = useCallback((idx: number, field: 'minutes' | 'type' | 'refPoint' | 'absoluteTime', value: string | number) => {
    setFormReminders(formReminders.map((r, i) => i === idx ? { ...r, [field]: field === 'minutes' ? (value === '' ? '' : Number(value)) : value } : r));
  }, [formReminders]);

  const removeReminder = useCallback((idx: number) => {
    setFormReminders(formReminders.filter((_, i) => i !== idx));
  }, [formReminders]);

  const toggleAssignee = useCallback((uid: number) => {
    if (formAssigneeIds.includes(uid)) {
      setFormAssigneeIds(formAssigneeIds.filter(id => id !== uid));
    } else {
      setFormAssigneeIds([...formAssigneeIds, uid]);
    }
  }, [formAssigneeIds]);

  const toggleWatcher = useCallback((uid: number) => {
    if (formWatcherIds.includes(uid)) {
      setFormWatcherIds(formWatcherIds.filter(id => id !== uid));
    } else {
      setFormWatcherIds([...formWatcherIds, uid]);
    }
  }, [formWatcherIds]);

  const handleSave = useCallback(async (closeAfter: boolean = true, silent: boolean = false) => {
    if (!formTitle.trim()) {
      if (!silent) showToast(t('calendarEventTitle') + '!', 'error');
      return;
    }
    if (formAssigneeIds.length === 0) {
      if (!silent) showToast(t('calendarAssignees') + '!', 'error');
      return;
    }

    if (!formAllDay && formType !== 'TASK') {
      const startMs = new Date(formStart).getTime();
      const endMs = new Date(formEnd).getTime();
      if (endMs < startMs + 10 * 60 * 1000) {
        if (!silent) showToast(t('calendarEndAfterStart'), 'error');
        return;
      }
    }

    if (formType === 'TASK' && formDueDateDate) {
      const startMs = new Date(formStart).getTime();
      const dueMs = new Date(combineDateTime(formDueDateDate, formDueDateTime || '00:00')).getTime();
      if (dueMs < startMs) {
        if (!silent) showToast(t('calendarDeadlineAfterStart'), 'error');
        return;
      }
    }

    if (silent) setAutoSaveState('saving'); else setSaving(true);
    const payload: Record<string, unknown> = {
      type: formType,
      title: formTitle.trim(),
      description: formDescription.trim() || null,
      startTime: formStart,
      endTime: formType === 'TASK' ? (formDueDateDate ? combineDateTime(formDueDateDate, formDueDateTime || '00:00') : null) : (formEnd || null),
      allDay: formAllDay,
      status: formStatus,
      priority: formPriority || null,
      location: formLocation || null,
      participants: formParticipants || null,
      phoneNumber: formPhone || null,
      callResult: formCallResult || null,
      imChannel: formImChannel || null,
      durationMinutes: formDuration ? parseInt(formDuration) : null,
      recurrenceRule: getRecurrenceRule(formRecurrence, formRecurrenceInterval),
      recurrenceEndDate: formRecurrenceEndDate ? combineDateTime(formRecurrenceEndDate, formRecurrenceEndTime || '00:00') : null,
      dueDate: formDueDateDate ? combineDateTime(formDueDateDate, formDueDateTime || '00:00') : null,
      color: formColor || null,
      checklist: formChecklist.map((item, i) => ({ text: item.text, done: item.done, sortOrder: i })),
      reminders: formReminders.map(r => ({ minutes: Number(r.minutes) || 0, type: r.type, refPoint: r.refPoint || 'START', absoluteTime: r.refPoint === 'ABSOLUTE' ? (r.absoluteTime || null) : null })),
      assigneeIds: formAssigneeIds,
      watcherIds: formWatcherIds,
      teamId: formTeamId,
      refType: formRefType || null,
      refId: formRefId || null,
      accountId: formAccountId,
      accountName: formAccountName,
      contactId: formContactId,
      contactName: formContactName,
      leadId: formLeadId,
      leadName: formLeadName,
      impulseType: formImpulseType,
      impulseKey: formImpulseKey,
      isPrivate: formPrivate,
      googleSync: formGoogleSync,
    };

    try {
      if (editingEvent && editingEvent.id) {
        const updated = await apiFetch<CalendarEvent>(`/api/user/calendar/events/${editingEvent.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (updated) {
          setEditingEvent(updated);
        }
        if (silent) {
          setAutoSaveState('saved');
          setTimeout(() => setAutoSaveState('idle'), 2000);
        } else {
          showToast((editLabel || t('calendarEdit')) + ' ✓', 'success');
        }
        onSaved?.(updated, false);
      } else {
        const created = await apiFetch<CalendarEvent>('/api/user/calendar/events', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (created?.id) {
          setEditingEvent(created);
        }
        if (silent) {
          setAutoSaveState('saved');
          setTimeout(() => setAutoSaveState('idle'), 2000);
        } else {
          showToast((newLabel || t('calendarSave')) + ' ✓', 'success');
        }
        onSaved?.(created, true);
      }
      if (closeAfter) {
        onClose?.();
      }
    } catch (err) {
      if (silent) {
        setAutoSaveState('error');
        setTimeout(() => setAutoSaveState('idle'), 3000);
      } else {
        const msg = err instanceof ApiError ? err.message : 'Error';
        showToast(msg, 'error');
      }
    } finally {
      if (!silent) setSaving(false);
    }
  }, [editingEvent, formType, formTitle, formDescription, formStart, formEnd, formAllDay, formStatus, formPriority, formLocation, formParticipants, formPhone, formCallResult, formImChannel, formDuration, formRecurrence, formRecurrenceInterval, formRecurrenceEndDate, formRecurrenceEndTime, formDueDateDate, formDueDateTime, formColor, formChecklist, formReminders, formAssigneeIds, formWatcherIds, formTeamId, formPrivate, formGoogleSync, formAccountId, formAccountName, formContactId, formContactName, formLeadId, formLeadName, formImpulseType, formImpulseKey, t, showToast, onClose, onSaved, editLabel, newLabel]);

  // Auto-save: debounce 2s after last change, only for existing events with title + assignees
  useEffect(() => {
    if (!active) return;
    if (!editingEvent?.id) return; // only existing events
    if (!isDirty) return;
    if (!formTitle.trim()) return; // skip if title empty
    if (formAssigneeIds.length === 0) return; // skip if no assignees
    if (saving || autoSaveState === 'saving') return;

    const timer = setTimeout(() => {
      handleSave(false, true); // silent auto-save, don't close
    }, 2000);

    return () => clearTimeout(timer);
  }, [active, editingEvent?.id, isDirty, formTitle, formAssigneeIds, saving, autoSaveState, handleSave]);

  const handleDelete = useCallback(async () => {
    if (deleteId === null) return;
    try {
      await apiFetch(`/api/user/calendar/events/${deleteId}`, { method: 'DELETE' });
      showToast(t('calendarDelete') + ' ✓', 'success');
      onDeleted?.(deleteId);
      setDeleteId(null);
      onClose?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error';
      showToast(msg, 'error');
    }
  }, [deleteId, t, showToast, onDeleted, onClose]);

  const cancelSnooze = useCallback(async () => {
    if (!editingEvent?.id) return;
    try {
      const updated = await apiFetch<CalendarEvent>(`/api/user/calendar/events/${editingEvent.id}/cancel-snooze`, { method: 'PATCH' });
      setEditingEvent(updated);
      onSaved?.(updated, false);
      showToast(t('snoozeCanceled'), 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Error', 'error');
    }
  }, [editingEvent, onSaved, showToast, t]);

  return {
    editingEvent, setEditingEvent, deleteId, setDeleteId, saving, autoSaveState,
    formType, setFormType, formTitle, setFormTitle, formDescription, setFormDescription,
    formAllDay, setFormAllDay, formStatus, setFormStatus, formPriority, setFormPriority,
    formLocation, setFormLocation, formParticipants, setFormParticipants,
    formPhone, setFormPhone, formCallResult, setFormCallResult, formImChannel, setFormImChannel,
    formDuration, setFormDuration, formRecurrence, setFormRecurrence,
    formRecurrenceInterval, setFormRecurrenceInterval,
    formRecurrenceEndDate, setFormRecurrenceEndDate, formRecurrenceEndTime, setFormRecurrenceEndTime,
    formDueDateDate, setFormDueDateDate, formDueDateTime, setFormDueDateTime,
    formColor, setFormColor, formChecklist, setFormChecklist, newChecklistText, setNewChecklistText,
    formReminders, setFormReminders, formAssigneeIds, setFormAssigneeIds,
    formWatcherIds, setFormWatcherIds, formTeamId, setFormTeamId,
    formPrivate, setFormPrivate, formGoogleSync, setFormGoogleSync, formAccountId, setFormAccountId,
    formAccountName, setFormAccountName, formAccountPohodaId, setFormAccountPohodaId,
    formContactId, setFormContactId,
    formContactName, setFormContactName, formLeadId, setFormLeadId,
    formLeadName, setFormLeadName, formRefType, formRefId, formImpulseType,
    formStartDate, setFormStartDate,
    formStartTime, setFormStartTime, formEndDate, setFormEndDate, formEndTime, setFormEndTime,
    formStart, formEnd, isDirty, checklistProgress,
    addChecklistItem, toggleChecklistItem, removeChecklistItem,
    addReminder, updateReminder, removeReminder,
    toggleAssignee, toggleWatcher, handleSave, handleDelete, cancelSnooze,
  };
}
